import endpoints from "../../../../utils/endpoints";
import type { FlowStep } from "../types";
import { FlowStepError } from "../engine/errors";
import {
  garaazTransitionPath,
  requireChild,
  requireEnquiry,
  supplierTransitionPath,
  verifyEq,
  waitForSnapshot,
} from "./common";

export const STEP_CANCEL_ENQUIRY = "cancel_enquiry";
export const STEP_CANCEL_ORDER = "cancel_order";
export const STEP_SUPPLIER_CANCEL = "supplier_cancel";
export const STEP_SUPPLIER_CBO = "supplier_cbo";
export const STEP_CUSTOMER_HOLD = "customer_hold";
export const STEP_CUSTOMER_UNHOLD = "customer_unhold";
export const STEP_ENQUIRY_CLOSE = "enquiry_close";

const cancelPath = (orderId: string) => `/${endpoints.cancelGaraazOrder(orderId)}`;

export const cancelEnquiryStep: FlowStep = {
  id: STEP_CANCEL_ENQUIRY,
  name: "Cancel enquiry",
  targetLabel: "Enquiry cancelled",
  description:
    "OrCA cancels the enquiry (POST /v1/orders/:id/cancel). Allowed from NEW/ACK/PART_CONVERSION_COMPLETED/DRAFT_ESTIMATE_SHARED/DRAFT_ESTIMATE_RESPONDED/FINAL_ESTIMATE_REQUESTED/FINAL_ESTIMATE_SHARED/BLOCKED/ALLOCATED/CBO when no confirmed child orders exist.",
  actor: "ORCA",
  kind: "api",
  estimateMs: [1000, 4000],
  execute: async (rt) => {
    const enquiry = requireEnquiry(rt.ctx);
    if (rt.ctx.child) {
      throw new FlowStepError(
        "Enquiry has a confirmed child order; the backend blocks enquiry cancellation. Cancel the order instead.",
        { expected: "no confirmed children", actual: `child ${rt.ctx.child.orderNo} exists` }
      );
    }
    await rt.api({
      label: "Cancel enquiry",
      method: "POST",
      path: cancelPath(enquiry.orderId),
      body: { cancellationReason: rt.ctx.config.cancellationReason },
    });
    await waitForSnapshot(rt, {
      trigger: "Enquiry cancel",
      expected: "enquiry.orderStatus = CANCELLED",
      check: (s) => s.enquiry?.orderStatus === "CANCELLED",
      describe: (s) => `enquiry=${s.enquiry?.orderStatus}`,
      maxAttempts: 3,
    });
  },
  verify: (s) => verifyEq("enquiry.orderStatus", "CANCELLED", s.enquiry?.orderStatus),
};

export const cancelOrderStep: FlowStep = {
  id: STEP_CANCEL_ORDER,
  name: "Cancel order (OrCA)",
  targetLabel: "Order cancelled",
  description:
    "OrCA cancels the confirmed order (POST /v1/orders/:id/cancel). Policy denies cancellation once the supplier is READY.",
  actor: "ORCA",
  kind: "api",
  estimateMs: [1000, 5000],
  execute: async (rt) => {
    const child = requireChild(rt.ctx);
    await rt.api({
      label: "Cancel order",
      method: "POST",
      path: cancelPath(child.orderId),
      body: { cancellationReason: rt.ctx.config.cancellationReason },
    });
    await waitForSnapshot(rt, {
      trigger: "Order cancel",
      expected: "order.orderStatus = CANCELLED",
      check: (s) => s.child?.orderStatus === "CANCELLED",
      describe: (s) => `order=${s.child?.orderStatus}, supplier=${s.child?.supplierStatus}`,
      maxAttempts: 3,
    });
  },
  verify: (s) => verifyEq("order.orderStatus", "CANCELLED", s.child?.orderStatus),
};

export const supplierCancelStep: FlowStep = {
  id: STEP_SUPPLIER_CANCEL,
  name: "Supplier cancels order",
  targetLabel: "Supplier cancelled",
  description:
    "Supplier cancels its allocation (transition?action=CANCEL with supplierCancellationReasons, every item given 0). On a full cancel the backend moves the order to SUPPLIER_CANCELLED (terminal).",
  actor: "SUPPLIER",
  kind: "api",
  estimateMs: [1000, 5000],
  execute: async (rt) => {
    const child = requireChild(rt.ctx);
    const token = await rt.getSupplierToken();
    const items = rt.ctx.snapshot?.child?.orderItems || [];
    await rt.api({
      label: "Supplier CANCEL",
      method: "POST",
      path: supplierTransitionPath(child.orderId, "CANCEL"),
      body: {
        supplierCancellationReasons: rt.ctx.config.cancellationReason,
        orderItems: items.map((i) => ({
          partId: i.partId,
          orderedQuantity: i.orderedQuantity,
          givenQuantity: 0,
          supplierCancellationReason: rt.ctx.config.cancellationReason,
        })),
      },
      actor: "SUPPLIER",
      authToken: token,
    });
    await waitForSnapshot(rt, {
      trigger: "Supplier cancel",
      expected: "allocatedTo.orderStatus = CANCELLED and order.orderStatus = SUPPLIER_CANCELLED",
      check: (s) =>
        ["CANCELLED", "CANCEL"].includes(s.child?.supplierStatus || "") && s.child?.orderStatus === "SUPPLIER_CANCELLED",
      describe: (s) => `supplier=${s.child?.supplierStatus}, order=${s.child?.orderStatus}`,
      maxAttempts: 3,
    });
  },
  verify: (s) => {
    const supplier = verifyEq("allocatedTo.orderStatus", ["CANCELLED", "CANCEL"], s.child?.supplierStatus);
    if (!supplier.ok) return supplier;
    return verifyEq("order.orderStatus", "SUPPLIER_CANCELLED", s.child?.orderStatus);
  },
};

export const supplierCboStep: FlowStep = {
  id: STEP_SUPPLIER_CBO,
  name: "Supplier marks CBO",
  targetLabel: "Supplier CBO",
  description: "Supplier marks the allocation as back-ordered (transition?action=CBO).",
  actor: "SUPPLIER",
  kind: "api",
  estimateMs: [1000, 4000],
  execute: async (rt) => {
    const child = requireChild(rt.ctx);
    const token = await rt.getSupplierToken();
    await rt.api({
      label: "Supplier CBO",
      method: "POST",
      path: supplierTransitionPath(child.orderId, "CBO"),
      body: {},
      actor: "SUPPLIER",
      authToken: token,
    });
    await waitForSnapshot(rt, {
      trigger: "Supplier CBO",
      expected: "allocatedTo.orderStatus = CBO",
      check: (s) => s.child?.supplierStatus === "CBO",
      describe: (s) => `supplier=${s.child?.supplierStatus}, order=${s.child?.orderStatus}`,
      maxAttempts: 3,
    });
  },
  verify: (s) => verifyEq("allocatedTo.orderStatus", "CBO", s.child?.supplierStatus),
};

export const customerHoldStep: FlowStep = {
  id: STEP_CUSTOMER_HOLD,
  name: "Customer hold",
  targetLabel: "Order on customer hold",
  description: "OrCA puts the order on customer hold (garaaz_transition?action=CUSTOMER_HOLD).",
  actor: "ORCA",
  kind: "api",
  estimateMs: [800, 3000],
  execute: async (rt) => {
    const target = rt.ctx.child ?? requireEnquiry(rt.ctx);
    await rt.api({
      label: "CUSTOMER_HOLD",
      method: "POST",
      path: garaazTransitionPath(target.orderId, "CUSTOMER_HOLD"),
      body: {},
    });
    await rt.api({
      label: "Set customerHoldStartTime",
      method: "PATCH",
      path: endpoints.updateOrderMetaInfo(target.orderId),
      body: { customerHoldStartTime: new Date().toISOString() },
    });
  },
  verify: (s, ctx) =>
    verifyEq(
      "orderStatus",
      "CUSTOMER_HOLD",
      ctx.child ? s.child?.orderStatus : s.enquiry?.orderStatus
    ),
};

export const customerUnholdStep: FlowStep = {
  id: STEP_CUSTOMER_UNHOLD,
  name: "Customer unhold",
  targetLabel: "Order resumed",
  description: "OrCA resumes the order (garaaz_transition?action=CUSTOMER_UNHOLD).",
  actor: "ORCA",
  kind: "api",
  estimateMs: [800, 3000],
  execute: async (rt) => {
    const target = rt.ctx.child ?? requireEnquiry(rt.ctx);
    await rt.api({
      label: "CUSTOMER_UNHOLD",
      method: "POST",
      path: garaazTransitionPath(target.orderId, "CUSTOMER_UNHOLD"),
      body: {},
    });
    await rt.api({
      label: "Set customerHoldEndTime",
      method: "PATCH",
      path: endpoints.updateOrderMetaInfo(target.orderId),
      body: { customerHoldEndTime: new Date().toISOString() },
    });
  },
  verify: (s, ctx) => {
    const status = ctx.child ? s.child?.orderStatus : s.enquiry?.orderStatus;
    return {
      ok: status !== undefined && status !== "CUSTOMER_HOLD",
      expected: "orderStatus != CUSTOMER_HOLD",
      actual: `orderStatus = ${status}`,
    };
  },
};

export const enquiryCloseStep: FlowStep = {
  id: STEP_ENQUIRY_CLOSE,
  name: "Close enquiry",
  targetLabel: "Enquiry closed",
  description: "OrCA closes the enquiry (garaaz_transition?action=ENQUIRY_CLOSED). Allowed from ACK.",
  actor: "ORCA",
  kind: "api",
  estimateMs: [800, 3000],
  execute: async (rt) => {
    const enquiry = requireEnquiry(rt.ctx);
    await rt.api({
      label: "ENQUIRY_CLOSED",
      method: "POST",
      path: garaazTransitionPath(enquiry.orderId, "ENQUIRY_CLOSED"),
      body: {},
    });
  },
  verify: (s) => verifyEq("enquiry.orderStatus", "ENQUIRY_CLOSED", s.enquiry?.orderStatus),
};
